import { supabase } from './supabaseClient';

function toBookingKey(value) {
  return String(value || '').trim();
}

export async function fetchOfficialTechLogsForBookings(bookingIds = []) {
  const keys = bookingIds.map(toBookingKey).filter(Boolean);
  if (keys.length === 0) return { data: [], error: null };

  return supabase
    .from('official_tech_logs')
    .select('booking_id, data, version, updated_at, updated_by')
    .in('booking_id', keys);
}

export async function fetchPreviousFlightMeterReading(bookingId) {
  const bookingKey = toBookingKey(bookingId);
  if (!bookingKey) {
    return {
      data: {
        ok: false,
        message: 'Missing booking identity.',
      },
      error: null,
    };
  }

  return supabase.rpc(
    'get_previous_flight_meter_reading',
    { p_booking_id: bookingKey }
  );
}

export function indexOfficialTechLogs(rows = []) {
  const logMap = {};
  const versionMap = {};

  rows.forEach((row) => {
    const key = toBookingKey(row.booking_id);
    if (!key) return;
    logMap[key] = row.data || {};
    versionMap[key] = Number(row.version) || 0;
  });

  return { logMap, versionMap };
}

export async function saveOfficialTechLogEntry({ bookingId, userId, entry, expectedVersion }) {
  const bookingKey = toBookingKey(bookingId);
  if (!bookingKey || !userId) {
    return {
      ok: false,
      message: 'Missing booking or user identity.',
    };
  }

  const { data, error } = await supabase.rpc('save_official_tech_log', {
    p_booking_id: bookingKey,
    p_user_id: userId,
    p_entry: entry || {},
    p_expected_version: Number.isFinite(Number(expectedVersion)) ? Number(expectedVersion) : null,
  });

  if (error) {
    return {
      ok: false,
      message: error.message || 'Could not save tech log.',
    };
  }

  return {
    ok: !!data?.ok,
    message: data?.message || '',
    version: Number(data?.version) || 0,
    updatedAt: data?.updated_at || null,
  };
}

export async function closeFlightWithOfficialTechLog({
  bookingId,
  entry,
  expectedStatus,
  expectedVersion,
}) {
  const bookingKey = toBookingKey(bookingId);
  if (!bookingKey) {
    return {
      ok: false,
      message: 'Missing booking identity.',
    };
  }

  const version =
    expectedVersion === null || expectedVersion === undefined
      ? null
      : Number(expectedVersion);

  const { data, error } = await supabase.rpc('close_flight_with_tech_log', {
    p_booking_id: bookingKey,
    p_entry: entry || {},
    p_expected_status: String(expectedStatus || ''),
    p_expected_version: Number.isFinite(version) ? version : null,
  });

  if (error) {
    return {
      ok: false,
      message: error.message || 'Could not close the flight.',
    };
  }

  return {
    ok: !!data?.ok,
    message: data?.message || '',
    status: data?.status || '',
    version: Number(data?.version) || 0,
    updatedAt: data?.updated_at || null,
  };
}

// Atomically saves the official tech log entry and marks the owning booking's
// Pilot Logbook stage complete in one database transaction. Only for genuine
// booking-linked rows; paper-<aircraftId> and manual-pilot-<userId>-<uuid>
// entries must keep using saveOfficialTechLogEntry() above.
export async function saveOfficialTechLogEntryAndCompleteBooking({ bookingId, entry, expectedVersion }) {
  const bookingKey = toBookingKey(bookingId);
  if (!bookingKey) {
    return {
      ok: false,
      message: 'Missing booking identity.',
    };
  }

  const { data, error } = await supabase.rpc('save_official_tech_log_and_complete_booking', {
    p_booking_id: bookingKey,
    p_entry: entry || {},
    p_expected_version: Number.isFinite(Number(expectedVersion)) ? Number(expectedVersion) : null,
  });

  if (error) {
    return {
      ok: false,
      message: error.message || 'Could not save and complete the pilot logbook entry.',
    };
  }

  return {
    ok: !!data?.ok,
    message: data?.message || '',
    version: Number(data?.version) || 0,
    updatedAt: data?.updated_at || null,
  };
}
